import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
  GridSingleSelectColDef
} from '@mui/x-data-grid-pro';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { Text } from '../../Atoms/Text';
import { HideConfirmationDialog, HideRecordButton } from '../../Compounds/HideConfirmationDialog';
import { HIDE_RECORD_PERMISSIONS } from '../../../store/GeneralStore';
import { getOpportunityQuotes, Quote } from '../../../Api/Orders/ordersApi';
import { standardOperators, tagsOperators } from '../../Compounds/UpstackDataGrid/helpers';
import { tagsColumn, TAGS_FIELD } from '../../Compounds/UpstackDataGrid/tagColumns';
import { EntityTagRenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/EntityTagRenderer';
import { SupportedTagEntityTypes } from '../../../store/tagEntityTypes';
import { GridPreferences, hasPermissions } from '../../../store/User';
import { useInventory } from '../../../store/Inventory';
import { isMonthToMonthTerm, MONTH_TO_MONTH_TERM, usaCurrency } from '../../../utils/helpers';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';

export interface QuoteApprovalSectionProps {
  opportunitySfId: string;
  // Quote rows carry no account of their own; every one of them belongs to this opportunity's.
  accountId?: string | null;
  onApprove: (quoteIds: string[]) => Promise<boolean>;
}

const APPROVED_QUOTE_HINT = 'Already approved and sent to UPSTACK, so it cannot be selected again.';
const HIDE_FIELD = 'hide';

const hideColumn = (onHide: (quote: Quote) => void): GridSingleSelectColDef => ({
  field: HIDE_FIELD,
  headerName: 'Hide',
  width: 60,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  type: 'singleSelect',
  valueOptions: [],
  display: 'flex',
  renderCell: (params: GridRenderCellParams) => (
    <HideRecordButton
      hidden={false}
      testId="hideQuoteButton"
      sfRecordLabel="Quote"
      onClick={() => onHide(params.row as Quote)}
    />
  )
});

const quoteBaseColumns: GridSingleSelectColDef[] = [
  {
    // Sits beside the checkbox because it is the reason that checkbox is disabled.
    field: 'approved',
    headerName: 'Approved',
    width: 80,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    display: 'flex',
    valueGetter: (value) => (value ? 'Approved' : ''),
    renderCell: (params: GridRenderCellParams) =>
      (params.row as Quote).approved ? (
        <Tooltip title={APPROVED_QUOTE_HINT}>
          <span
            aria-label="Approved"
            className="text-green-600 text-lg leading-none">
            &#10003;
          </span>
        </Tooltip>
      ) : null
  },
  {
    field: 'service',
    headerName: 'Quote',
    width: 180,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators()
  },
  {
    field: 'supplier',
    headerName: 'Supplier',
    width: 160,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators()
  },
  {
    field: 'term',
    headerName: 'Term',
    width: 130,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    valueFormatter: (value) => {
      // A quote with no term stays blank; only assets carry Salesforce's 0-means-month-to-month convention.
      if (!value) return '';
      if (isMonthToMonthTerm(value)) return MONTH_TO_MONTH_TERM;
      return `${value} Months`;
    }
  },
  {
    field: 'mrc',
    headerName: 'MRC',
    width: 130,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    valueFormatter: (value) => (value ? usaCurrency.format(Number(value)) : '')
  },
  {
    field: 'nrc',
    headerName: 'NRC',
    width: 130,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    valueFormatter: (value) => (value ? usaCurrency.format(Number(value)) : '')
  },
  {
    field: 'notes',
    headerName: 'UPSTACK Notes',
    width: 280,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators()
  }
];

// The tags cell refetches through this section's own state, so the column set is built per instance.
export const quoteColumns = (
  refetchQuotes: () => Promise<void>,
  accountId?: string | null,
  onHide?: (quote: Quote) => void
): GridSingleSelectColDef[] => [
  ...(onHide ? [hideColumn(onHide)] : []),
  ...quoteBaseColumns,
  tagsColumn({
    filterOperators: tagsOperators(),
    renderer: EntityTagRenderer,
    entityType: SupportedTagEntityTypes.QUOTE,
    entityAccountId: accountId ? () => accountId : undefined,
    refetch: refetchQuotes
  })
];

const quoteGridSettings = (canHide: boolean): GridPreferences => {
  const fields = [...(canHide ? [HIDE_FIELD] : []), ...quoteBaseColumns.map((c) => c.field), TAGS_FIELD];
  return {
    muiConfig: {
      columns: {
        columnVisibilityModel: Object.fromEntries(fields.map((field) => [field, true])),
        // '__check__' keeps the row-selection checkbox column on the left
        orderedFields: ['__check__', ...fields],
        dimensions: {}
      },
      pinnedColumns: { left: ['__check__'] },
      sorting: { sortModel: [] },
      pagination: { paginationModel: { page: 0, pageSize: 50 } }
    } as GridPreferences['muiConfig'],
    disabledFields: []
  };
};

export function QuoteApprovalSection({ opportunitySfId, accountId, onApprove }: QuoteApprovalSectionProps) {
  const theme = useTheme();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [selectedQuoteIds, setSelectedQuoteIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [justApproved, setJustApproved] = useState(false);
  // Remount the grid after an approval so its internal selection clears and
  // the newly-approved rows render as non-selectable.
  const [gridKey, setGridKey] = useState(0);
  const [quoteToHide, setQuoteToHide] = useState<Quote | null>(null);
  const { createHiddenAsset } = useInventory();
  // Without an account the hide would still land, but the Hidden Records refresh after it would be asked for no account.
  const canHide = !!accountId && hasPermissions(HIDE_RECORD_PERMISSIONS);

  const fetchQuotes = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      const { data, error } = await getOpportunityQuotes(opportunitySfId);
      if (error) {
        setFetchError(true);
        setQuotes([]);
      } else {
        setFetchError(false);
        setQuotes(data || []);
      }
      if (showSpinner) setLoading(false);
    },
    [opportunitySfId]
  );

  const handleHideClick = useCallback((quote: Quote) => setQuoteToHide(quote), []);

  // Spinnerless: the spinner replaces the grid, and a remount drops the selection the Approve button still holds.
  const columns = useMemo(
    () => quoteColumns(() => fetchQuotes(false), accountId, canHide ? handleHideClick : undefined),
    [fetchQuotes, accountId, canHide, handleHideClick]
  );
  const gridSettings = useMemo(() => quoteGridSettings(canHide), [canHide]);

  useEffect(() => {
    setQuotes([]);
    setSelectedQuoteIds([]);
    setJustApproved(false);
    setConfirmOpen(false);
    setFetchError(false);
    fetchQuotes();
  }, [opportunitySfId, fetchQuotes]);

  // Quotes already approved (in a prior visit) cannot be re-selected or re-approved.
  const allApproved = quotes.length > 0 && quotes.every((quote) => quote.approved);

  const onRowSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    setSelectedQuoteIds(rowSelectionModel.map(String));
  };

  const handleApproveClick = () => {
    if (selectedQuoteIds.length === 0) return;
    setConfirmOpen(true);
  };

  const handleHide = async () => {
    const quote = quoteToHide;
    setQuoteToHide(null);
    // The server files the hide under the quote's own account and ignores the one sent.
    if (!quote || !accountId || !(await createHiddenAsset(quote.id, accountId, 'Quote'))) return;
    // The grid drops a vanished row from its own selection; the Approve button's copy has to follow it.
    setSelectedQuoteIds((ids) => ids.filter((id) => id !== quote.id));
    await fetchQuotes(false);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    const success = await onApprove(selectedQuoteIds);
    setSubmitting(false);
    setConfirmOpen(false);
    if (success) {
      setSelectedQuoteIds([]);
      setJustApproved(true);
      setGridKey((key) => key + 1);
      await fetchQuotes();
    }
  };

  return (
    <>
      <DetailViewCard
        collapsed={false}
        enableCollapse
        key="quotes"
        title={`Quotes${quotes.length ? `  ${quotes.length} Items` : ''}`}>
        <Tabs
          value={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#F9F9F9',
            margin: '-20px',
            marginBottom: '12px',
            '.Mui-selected': {
              backgroundColor: 'white',
              color: theme.palette.primary.main
            }
          }}
          TabIndicatorProps={{
            sx: {
              backgroundColor: 'white',
              height: 2,
              borderRadius: 1
            }
          }}>
          <Tab
            label="New Order Requests"
            sx={{ textTransform: 'none', fontSize: '.8rem', color: 'black' }}
          />
        </Tabs>
        {justApproved && (
          <div className="flex items-center gap-3 mb-3">
            <span className="text-green-600 text-2xl leading-none">&#10003;</span>
            <div>
              <Text
                size="sm14"
                className="font-semibold">
                Approval Received
              </Text>
              <Text
                size="sm14"
                color="grey4">
                Your UPSTACK team has been notified and will proceed with processing your order.
              </Text>
            </div>
          </div>
        )}
        {fetchError && <div className="text-center text-red-500 py-4">Failed to load quotes. Please try again.</div>}
        <UpstackDataGrid
          key={gridKey}
          dataCy="quotes-data-grid"
          title=""
          rows={quotes}
          columns={columns}
          loadingData={loading}
          page="quote_grid"
          gridSettings={gridSettings}
          showSearch={true}
          enableRowSelection={true}
          isRowSelectable={(params: GridRowParams) => !(params.row as Quote).approved}
          onRowSelectionModelChange={onRowSelectionModelChange}
          containerClassName="h-[24rem]"
        />
        <div className="flex items-center justify-end gap-4 mt-4 pt-4 border-t">
          {!allApproved && (
            <Text
              size="sm14"
              color="grey4">
              Please select the quotes you&apos;d like UPSTACK to Order and then Approve
            </Text>
          )}
          <button
            disabled={allApproved || selectedQuoteIds.length === 0}
            onClick={handleApproveClick}
            className={DEFAULT_ADVANCE_BUTTON_STYLE}>
            {allApproved ? 'Approval Sent' : 'Approve'}
          </button>
        </div>
      </DetailViewCard>

      <HideConfirmationDialog
        open={!!quoteToHide}
        onClose={() => setQuoteToHide(null)}
        onConfirm={handleHide}
        type="Hide"
        recordType="Quote"
      />

      <Dialog
        open={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}>
        <DialogTitle>Confirm Quote Approval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve the selected quote{selectedQuoteIds.length > 1 ? 's' : ''}? This will
            notify UPSTACK to proceed with your order.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <button
            onClick={() => setConfirmOpen(false)}
            disabled={submitting}
            className={DEFAULT_CANCEL_BUTTON_STYLE}>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            aria-label={submitting ? 'Approving' : undefined}
            className={`${DEFAULT_ADVANCE_BUTTON_STYLE} min-w-20`}>
            {submitting ? (
              <CircularProgress
                size={14}
                color="inherit"
              />
            ) : (
              'Approve'
            )}
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
}
